import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ArrowLeft, Plus, CheckSquare, Calendar, UserPlus, X, KanbanSquare, List, MessageSquare, Settings2, Lock, AlertTriangle, GanttChartSquare, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { realtime } from "../lib/ws";
import { Avatar, PRIORITIES, dueState, statusRule } from "../lib/helpers";
import TaskModal from "../components/TaskModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FilterBar } from "../components/FilterBar";
import { ListView } from "../components/ListView";
import { BoardChat } from "../components/BoardChat";
import { BoardSettings } from "../components/BoardSettings";
import { AutomationsPanel } from "../components/AutomationsPanel";
import { CalendarView } from "../components/CalendarView";
import { TimelineView } from "../components/TimelineView";
import { PageSkeleton } from "../components/Skeleton";
import { celebrate } from "../lib/confetti";

const COL_PALETTE = ["#00B0FF", "#FFD600", "#FF1744", "#00E676", "#AB47BC", "#FF6D00"];
const DUE_CLS = { overdue: "text-red-400", soon: "text-amber-400" };

function TaskCard({ task, users, onClick, blocked, slaBreach, inProgressCol, delay = 0 }) {
  const assignees = (task.assignee_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const pr = PRIORITIES[task.priority] || PRIORITIES.medium;
  const subs = task.subtasks || [];
  const doneSubs = subs.filter((s) => s.done).length;
  const ds = dueState(task.due_date);
  return (
    <div onClick={onClick} data-testid={`task-card-${task.id}`}
      style={{ ...(task.color ? { borderLeft: `3px solid ${task.color}` } : {}), animationDelay: `${delay}ms` }}
      className="bg-[#1C1C1F] border border-white/10 rounded-lg p-3.5 hover:border-white/25 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.45)] transition-[transform,border-color,box-shadow] duration-200 cursor-pointer group animate-fade-up">
      <div className="flex items-start gap-2">
        <span className="h-1.5 w-1.5 rounded-full mt-1.5 shrink-0" style={{ background: pr.color }} />
        <p className="text-sm font-medium leading-snug flex-1">{task.title}</p>
        {blocked && <Lock data-testid={`blocked-badge-${task.id}`} className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" title="Bloqueada por otra tarea" />}
        {slaBreach && <AlertTriangle data-testid={`sla-badge-${task.id}`} className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" title="SLA superado" />}
      </div>
      {task.labels?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2.5">
          {task.labels.map((l) => <span key={l} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 text-zinc-300">{l}</span>)}
        </div>
      )}
      {inProgressCol && (
        <div className="mt-2.5" data-testid={`progress-bar-${task.id}`}>
          <div className="flex justify-between text-[10px] text-zinc-500 mb-0.5"><span>Avance</span><span className="font-semibold text-[#FFD600]">{task.progress || 0}%</span></div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-[#FFD600] rounded-full" style={{ width: `${task.progress || 0}%` }} /></div>
        </div>
      )}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2.5 text-xs text-zinc-500">
          {subs.length > 0 && <span className="flex items-center gap-1"><CheckSquare className="h-3.5 w-3.5" />{doneSubs}/{subs.length}</span>}
          {task.due_date && <span className={`flex items-center gap-1 ${DUE_CLS[ds] || ""}`}><Calendar className="h-3.5 w-3.5" />{new Date(task.due_date).toLocaleDateString("es", { day: "numeric", month: "short" })}</span>}
        </div>
        <div className="flex -space-x-2">
          {assignees.slice(0, 3).map((u) => <Avatar key={u.id} user={u} size={22} className="ring-2 ring-[#1C1C1F]" />)}
        </div>
      </div>
    </div>
  );
}

export default function BoardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);
  const [adding, setAdding] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [fAssignee, setFAssignee] = useState("");
  const [colDialog, setColDialog] = useState(null);
  const [colName, setColName] = useState("");
  const [colSla, setColSla] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showAutomations, setShowAutomations] = useState(false);
  const [taskTemplates, setTaskTemplates] = useState([]);

  useEffect(() => { api.get("/task-templates").then((r) => setTaskTemplates(r.data)).catch(() => {}); }, []);
  const [warnMsg, setWarnMsg] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, t, u] = await Promise.all([
        api.get(`/boards/${id}`),
        api.get(`/boards/${id}/tasks`),
        api.get(`/users`),
      ]);
      setBoard(b.data); setTasks(t.data); setUsers(u.data);
    } catch (e) {
      toast.error("No se pudo cargar el tablero");
      navigate("/boards");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    realtime.subscribeBoard(id);
    const off = realtime.on((msg) => {
      if (msg.type === "task_created" && msg.task?.board_id === id) {
        setTasks((prev) => (prev.some((t) => t.id === msg.task.id) ? prev : [...prev, msg.task]));
      } else if (msg.type === "task_updated" && msg.task?.board_id === id) {
        setTasks((prev) => prev.map((t) => (t.id === msg.task.id ? msg.task : t)));
      } else if (msg.type === "task_moved") {
        setTasks((prev) => prev.map((t) => (t.id === msg.task_id ? { ...t, status: msg.status, order: msg.order } : t)));
      } else if (msg.type === "task_deleted") {
        setTasks((prev) => prev.filter((t) => t.id !== msg.task_id));
        setActive((a) => (a && a.id === msg.task_id ? null : a));
      } else if (msg.type === "board_updated" && msg.board?.id === id) {
        setBoard(msg.board);
      }
    });
    return () => { off(); realtime.unsubscribeBoard(id); };
  }, [id]);

  const matchesFilters = (t) =>
    (!q || t.title.toLowerCase().includes(q.toLowerCase()) || (t.labels || []).some((l) => l.toLowerCase().includes(q.toLowerCase()))) &&
    (!fPriority || t.priority === fPriority) &&
    (!fAssignee || (t.assignee_ids || []).includes(fAssignee));

  const filteredTasks = tasks.filter(matchesFilters);
  const colTasks = (colId) => filteredTasks.filter((t) => t.status === colId).sort((a, b) => a.order - b.order);

  const applyMove = async (draggableId, statusId, newOrder, srcColId) => {
    setTasks((prev) => prev.map((t) => t.id === draggableId ? { ...t, status: statusId, order: newOrder } : t));
    const destCol = board.columns.find((c) => c.id === statusId);
    const srcCol = board.columns.find((c) => c.id === srcColId);
    if (destCol?.title.toLowerCase().startsWith("complet") && !srcCol?.title.toLowerCase().startsWith("complet")) celebrate();
    try {
      await api.put(`/tasks/${draggableId}/move`, { status: statusId, order: newOrder });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo mover la tarea");
      load();
    }
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const destTasks = colTasks(destination.droppableId).filter((t) => t.id !== draggableId);
    let newOrder;
    if (destTasks.length === 0) newOrder = 0;
    else if (destination.index <= 0) newOrder = destTasks[0].order - 1;
    else if (destination.index >= destTasks.length) newOrder = destTasks[destTasks.length - 1].order + 1;
    else newOrder = (destTasks[destination.index - 1].order + destTasks[destination.index].order) / 2;

    if (destination.droppableId !== source.droppableId) {
      const rule = statusRule(board.columns, source.droppableId, destination.droppableId);
      if (rule === "block-back") { setWarnMsg("La tarea ya fue iniciada y no puede regresar al estado «Planeado»."); return; }
      if (rule === "confirm-start") { setPendingMove({ draggableId, statusId: destination.droppableId, newOrder, srcColId: source.droppableId }); return; }
    }
    applyMove(draggableId, destination.droppableId, newOrder, source.droppableId);
  };

  const addTask = async (colId) => {
    if (!newTitle.trim()) { setAdding(null); return; }
    try {
      const { data } = await api.post("/tasks", { board_id: id, title: newTitle, status: colId });
      setTasks((t) => (t.some((x) => x.id === data.id) ? t : [...t, data]));
      setNewTitle(""); setAdding(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo crear la tarea"); }
  };

  const submitColDialog = async () => {
    const title = colName.trim();
    if (!title) return;
    const sla = colSla ? parseFloat(colSla) : null;
    const cols = colDialog.mode === "add"
      ? [...board.columns, { id: crypto.randomUUID(), title, color: COL_PALETTE[board.columns.length % COL_PALETTE.length], sla_hours: sla }]
      : board.columns.map((c) => c.id === colDialog.col.id ? { ...c, title, sla_hours: sla } : c);
    try {
      const { data } = await api.put(`/boards/${id}`, { columns: cols });
      setBoard(data);
      setColDialog(null);
    } catch { toast.error("No se pudo guardar la columna"); }
  };

  const deleteColumn = async (col) => {
    if (tasks.some((t) => t.status === col.id)) { toast.error("Mueve o elimina las tareas primero"); return; }
    const cols = board.columns.filter((c) => c.id !== col.id);
    const { data } = await api.put(`/boards/${id}`, { columns: cols });
    setBoard(data);
  };

  const toggleMember = async (uid) => {
    const set = new Set(board.member_ids || []);
    set.has(uid) ? set.delete(uid) : set.add(uid);
    const { data } = await api.put(`/boards/${id}`, { member_ids: [...set] });
    setBoard(data);
  };

  if (!board) return <PageSkeleton rows={3} />;
  const members = (board.member_ids || []).map((mid) => users.find((u) => u.id === mid)).filter(Boolean);
  const doneColIds = new Set(board.columns.filter((c) => c.title.toLowerCase().startsWith("complet")).map((c) => c.id));
  const isBlocked = (t) => (t.blocked_by || []).some((bid) => {
    const bt = tasks.find((x) => x.id === bid);
    return bt && !doneColIds.has(bt.status);
  });
  const slaBreached = (t) => {
    if (doneColIds.has(t.status)) return false;
    const col = board.columns.find((c) => c.id === t.status);
    if (!col?.sla_hours) return false;
    return (Date.now() - new Date(t.status_changed_at || t.created_at).getTime()) / 3600000 > col.sla_hours;
  };
  const progressColIds = new Set(board.columns.filter((c) => c.title.toLowerCase().includes("progreso")).map((c) => c.id));

  return (
    <div className="h-screen flex flex-col">
      <header className="shrink-0 px-3 sm:px-6 lg:px-8 py-3 sm:py-4 border-b border-white/10 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate("/boards")} data-testid="back-to-boards" className="text-zinc-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
            <div className="min-w-0">
              <h1 className="font-display text-xl font-extrabold truncate">{board.name}</h1>
              {board.description && <p className="text-xs text-zinc-500 truncate">{board.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <div className="hidden sm:flex -space-x-2">
              {members.slice(0, 5).map((u) => <Avatar key={u.id} user={u} size={30} className="ring-2 ring-[#0A0A0B]" />)}
            </div>
            <button onClick={() => setShowAutomations(true)} data-testid="board-automations-button" title="Automatizaciones" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-2 sm:px-3 py-2 text-sm flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-[#FFD600]" />
            </button>
            <button onClick={() => setShowSettings(true)} data-testid="board-settings-button" title="Configuración" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-2 sm:px-3 py-2 text-sm flex items-center gap-1.5">
              <Settings2 className="h-4 w-4" />
            </button>
            <button onClick={() => setShowChat(true)} data-testid="board-chat-button" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-2 sm:px-3 py-2 text-sm flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4" /> <span className="hidden sm:inline">Chat</span>
            </button>
            <button onClick={() => setShowMembers(true)} data-testid="manage-members-button" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm flex items-center gap-1.5">
              <UserPlus className="h-4 w-4" /> <span className="hidden sm:inline">Miembros</span>
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <FilterBar q={q} setQ={setQ} fPriority={fPriority} setFPriority={setFPriority} fAssignee={fAssignee} setFAssignee={setFAssignee} users={users} />
          <div className="flex items-center bg-[#121214] border border-white/10 rounded-lg p-0.5">
            {[["kanban", KanbanSquare, "Kanban"], ["list", List, "Lista"], ["calendar", Calendar, "Calendario"], ["timeline", GanttChartSquare, "Cronograma"]].map(([v, Icon, label]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}-button`}
                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${view === v ? "bg-primary text-white" : "text-zinc-400 hover:text-white"}`}>
                <Icon className="h-3.5 w-3.5" /> <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {view === "list" ? (
        <div className="flex-1 min-h-0">
          <ListView board={board} tasks={filteredTasks} users={users} onOpen={setActive} />
        </div>
      ) : view === "calendar" ? (
        <div className="flex-1 min-h-0">
          <CalendarView tasks={filteredTasks} onOpen={setActive} />
        </div>
      ) : view === "timeline" ? (
        <div className="flex-1 min-h-0">
          <TimelineView board={board} tasks={filteredTasks} onOpen={setActive} />
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex-1 overflow-x-auto overflow-y-hidden p-3 sm:p-6 lg:p-8">
            <div className="flex gap-5 h-full items-start">
              {board.columns.map((col, ci) => (
                <div key={col.id} data-testid={`column-${col.id}`} style={{ animationDelay: `${ci * 70}ms` }} className="flex flex-col w-[280px] min-w-[280px] sm:w-[300px] sm:min-w-[300px] max-h-full bg-[#0E0E10] rounded-xl border border-white/5 animate-fade-in-soft">
                  <div className="flex items-center gap-2 px-3.5 py-3 group">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.color }} />
                    <h3 onClick={() => { setColDialog({ mode: "rename", col }); setColName(col.title); setColSla(col.sla_hours || ""); }} data-testid={`rename-column-${col.id}`} className="font-semibold text-sm flex-1 cursor-pointer hover:text-primary truncate" title="Clic para renombrar">{col.title}</h3>
                    <span className="text-xs text-zinc-500 bg-white/5 rounded-full px-2 py-0.5">{colTasks(col.id).length}</span>
                    <button onClick={() => deleteColumn(col)} className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}
                        className={`flex-1 overflow-y-auto px-2.5 pb-2 space-y-2.5 min-h-[60px] rounded-lg transition-colors ${snapshot.isDraggingOver ? "bg-white/5 ring-1 ring-inset ring-white/15" : ""}`}>
                        {colTasks(col.id).map((task, idx) => (
                          <Draggable key={task.id} draggableId={task.id} index={idx}>
                            {(prov, snap) => (
                              <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                                style={{ ...prov.draggableProps.style }}>
                                <div className={snap.isDragging ? "rotate-2 scale-105 rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.55)]" : ""}>
                                  <TaskCard task={task} users={users} onClick={() => setActive(task)} blocked={isBlocked(task)} slaBreach={slaBreached(task)} inProgressCol={progressColIds.has(task.status)} delay={Math.min(idx * 45, 400)} />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                  <div className="p-2.5">
                    {adding === col.id ? (
                      <div className="space-y-2">
                        <textarea autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(col.id); } if (e.key === "Escape") setAdding(null); }}
                          data-testid="new-task-input" placeholder="Título de la tarea…" rows={2}
                          className="w-full bg-[#1C1C1F] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary resize-none" />
                        <div className="flex gap-2">
                          <button onClick={() => addTask(col.id)} data-testid="confirm-add-task" className="bg-primary hover:bg-[#FF5047] text-white text-sm font-semibold rounded-lg px-3 py-1.5">Añadir</button>
                          <button onClick={() => { setAdding(null); setNewTitle(""); }} className="text-zinc-500 hover:text-white text-sm px-2">Cancelar</button>
                        </div>
                        {taskTemplates.length > 0 && (
                          <select data-testid="task-template-select" defaultValue=""
                            onChange={async (e) => {
                              const tid = e.target.value;
                              if (!tid) return;
                              try {
                                const { data } = await api.post(`/task-templates/${tid}/apply`, { board_id: id, status: col.id });
                                setTasks((t) => (t.some((x) => x.id === data.id) ? t : [...t, data]));
                                setAdding(null);
                              } catch { toast.error("No se pudo aplicar la plantilla"); }
                            }}
                            className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-400 outline-none focus:border-primary">
                            <option value="">…o crear desde plantilla</option>
                            {taskTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        )}
                      </div>
                    ) : (
                      <button onClick={() => { setAdding(col.id); setNewTitle(""); }} data-testid={`add-task-${col.id}`}
                        className="w-full flex items-center gap-2 text-sm text-zinc-500 hover:text-white hover:bg-white/5 rounded-lg px-3 py-2 transition-colors">
                        <Plus className="h-4 w-4" /> Añadir tarea
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button onClick={() => { setColDialog({ mode: "add" }); setColName(""); setColSla(""); }} data-testid="add-column-button"
                className="w-[280px] min-w-[280px] shrink-0 border border-dashed border-white/10 rounded-xl px-4 py-3 text-sm text-zinc-500 hover:text-white hover:border-white/25 transition-colors flex items-center gap-2">
                <Plus className="h-4 w-4" /> Añadir columna
              </button>
            </div>
          </div>
        </DragDropContext>
      )}

      {active && (
        <TaskModal task={active} board={board} users={users} onClose={() => setActive(null)}
          onUpdated={(u) => setTasks((t) => t.map((x) => x.id === u.id ? u : x))}
          onDeleted={(tid) => setTasks((t) => t.filter((x) => x.id !== tid))} />
      )}

      {showChat && <BoardChat boardId={id} onClose={() => setShowChat(false)} />}
      {showSettings && <BoardSettings board={board} onClose={() => setShowSettings(false)} onUpdated={setBoard} />}
      {showAutomations && <AutomationsPanel board={board} users={users} onClose={() => setShowAutomations(false)} />}

      {colDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setColDialog(null)}>
          <div className="w-full max-w-sm bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="column-dialog">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">{colDialog.mode === "add" ? "Nueva columna" : "Renombrar columna"}</h2>
              <button onClick={() => setColDialog(null)}><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
            </div>
            <input autoFocus value={colName} onChange={(e) => setColName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitColDialog(); if (e.key === "Escape") setColDialog(null); }}
              data-testid="column-name-input" placeholder="Nombre de la columna"
              className="w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" />
            <input value={colSla} onChange={(e) => setColSla(e.target.value)} type="number" step="1" min="0"
              data-testid="column-sla-input" placeholder="SLA en horas (opcional, avisa si una tarea pasa demasiado tiempo aquí)"
              className="mt-2 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setColDialog(null)} className="text-sm text-zinc-400 hover:text-white px-3 py-2">Cancelar</button>
              <button onClick={submitColDialog} data-testid="column-dialog-submit" className="bg-primary hover:bg-[#FF5047] text-white text-sm font-semibold rounded-lg px-4 py-2">
                {colDialog.mode === "add" ? "Crear" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMembers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowMembers(false)}>
          <div className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">Miembros del tablero</h2>
              <button onClick={() => setShowMembers(false)}><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
            </div>
            <div className="space-y-2">
              {users.map((u) => {
                const inBoard = (board.member_ids || []).includes(u.id);
                const isOwner = u.id === board.owner_id;
                return (
                  <div key={u.id} className="flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2.5">
                    <Avatar user={u} size={36} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                    </div>
                    {isOwner ? (
                      <span className="text-xs font-semibold text-primary">Propietario</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        {inBoard && (
                          <select value={(board.roles || {})[u.id] || "editor"} data-testid={`role-select-${u.id}`}
                            onChange={async (e) => {
                              const roles = { ...(board.roles || {}), [u.id]: e.target.value };
                              const { data } = await api.put(`/boards/${id}`, { roles });
                              setBoard(data);
                            }}
                            className="bg-[#121214] border border-white/10 rounded-lg text-xs px-2 py-1.5 outline-none">
                            <option value="editor">Editor</option>
                            <option value="viewer">Lector</option>
                          </select>
                        )}
                        <button onClick={() => toggleMember(u.id)} data-testid={`toggle-member-${u.id}`}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${inBoard ? "bg-white/5 text-zinc-300 hover:bg-red-500/15 hover:text-red-400" : "bg-primary text-white hover:bg-[#FF5047]"}`}>
                          {inBoard ? "Quitar" : "Añadir"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {warnMsg && (
        <ConfirmDialog title="Acción no permitida" message={warnMsg} confirmLabel="Entendido" cancelLabel={null} tone="warn"
          onConfirm={() => setWarnMsg(null)} onCancel={() => setWarnMsg(null)} />
      )}
      {pendingMove && (
        <ConfirmDialog title="¿Iniciar tarea?"
          message="La tarea tomará la fecha de hoy como inicio, desaparecerá del estado «Planeado» y ya no podrá regresar al estado inicial. ¿Estás seguro?"
          confirmLabel="Sí, iniciar" tone="warn"
          onConfirm={() => { applyMove(pendingMove.draggableId, pendingMove.statusId, pendingMove.newOrder, pendingMove.srcColId); setPendingMove(null); }}
          onCancel={() => setPendingMove(null)} />
      )}
    </div>
  );
}
